import React from "react";
import { PageHero } from "../components/PageHero";
import { CTAStrip } from "../components/CTAStrip";
import { Link } from "react-router-dom";
import { ClipboardList, CalendarCheck, GraduationCap } from "lucide-react";

const programs = [
  {
    name: "Playgroup",
    tag: "1–2 yrs",
    bg: "bg-[#DCE7FF]",
    icon: "🧱",
    desc: "A warm, nurturing space where toddlers take their very first steps into structured learning. Through sensory exploration, gentle movement, and guided play, children begin building social awareness and confidence — always with caregiver support close by.",
    timings: "Mon–Fri · 9:00 am – 12:00 pm",
    highlights: ["Sensory exploration activities", "Music, rhythm & movement", "Interactive story time", "Social bonding & sharing", "Fine motor skill development"],
  },
  {
    name: "Nursery",
    tag: "2–3 yrs",
    bg: "bg-[#FFE1E1]",
    icon: "🎨",
    desc: "Designed to spark imagination and curiosity, our Nursery program supports early language development, emotional growth, and creative thinking in a joyful, child-friendly environment. Every day is a new adventure of discovery.",
    timings: "Mon–Fri · 9:00 am – 1:00 pm",
    highlights: ["Art, craft & creative expression", "Early language & vocabulary", "Outdoor exploration & play", "Emotional & social development", "Pre-writing & drawing skills"],
  },
  {
    name: "Kindergarten",
    tag: "3–5 yrs",
    bg: "bg-[#FFF0DB]",
    icon: "📚",
    desc: "A structured yet playful curriculum that lays strong foundations in literacy, numeracy, and critical thinking. Our Kindergarten program prepares children for primary school with confidence, curiosity, and a genuine love for learning.",
    timings: "Mon–Fri · 9:00 am – 3:00 pm",
    highlights: ["Phonics & early reading", "Number concepts & basic math", "STEM exploration & problem solving", "Group projects & collaboration", "Physical education & yoga"],
  },
  {
    name: "Daycare",
    tag: "1–5 yrs",
    bg: "bg-[#E6F8E4]",
    icon: "🧸",
    desc: "A safe, loving full-day environment where your child is cared for, engaged, and celebrated. With nutritious home-style meals, structured rest periods, and enriching activities throughout the day, you can work with complete peace of mind.",
    timings: "Mon–Fri · 8:00 am – 6:00 pm",
    highlights: ["Nutritious meals & evening snack", "Structured nap & rest time", "Daily learning activity schedule", "Real-time parent updates", "Trained & background-verified caregivers"],
  },
];

const ageGuide = [
  { age: "1–2 yrs", program: "Playgroup", emoji: "🧱", bg: "bg-[#DCE7FF]" },
  { age: "2–3 yrs", program: "Nursery", emoji: "🎨", bg: "bg-[#FFE1E1]" },
  { age: "3–5 yrs", program: "Kindergarten", emoji: "📚", bg: "bg-[#FFF0DB]" },
  { age: "1–5 yrs", program: "Daycare", emoji: "🧸", bg: "bg-[#E6F8E4]" },
];

const admissionSteps = [
  { icon: ClipboardList, step: "01", title: "Submit an Enquiry", desc: "Fill out our online enquiry form or call us directly. Tell us your child's age and which program interests you.", color: "text-kid-lilac", tint: "bg-[#DCE7FF]" },
  { icon: CalendarCheck, step: "02", title: "Schedule a Visit", desc: "We'll invite you for a free guided tour of the school. See the classrooms, meet the teachers, and get all your questions answered.", color: "text-kid-amber", tint: "bg-[#FFF0DB]" },
  { icon: GraduationCap, step: "03", title: "Complete Admission", desc: "Once you're happy, complete the simple admission formalities with our team and get your child's journey started!", color: "text-emerald-500", tint: "bg-[#E6F8E4]" },
];

export default function Programs() {
  return (
    <div data-testid="page-programs">
      <PageHero title="Our Programs" breadcrumb="Programs" />

      {/* Age guide */}
      <section className="bg-kid-bg py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-center text-kid-navy/70 font-semibold mb-6">Which program suits my child?</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ageGuide.map((g) => (
              <div key={g.program} className={`${g.bg} rounded-2xl p-5 text-center`} data-testid={`age-guide-${g.program.toLowerCase()}`}>
                <div className="text-4xl">{g.emoji}</div>
                <p className="mt-2 font-bold text-kid-navy">{g.program}</p>
                <p className="text-xs text-kid-navy/60 mt-1">{g.age}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program cards */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-kid-navy">Age-appropriate programs built for every milestone</h2>
            <p className="mt-4 text-kid-navy/70 text-lg">
              At Spring Kids, every program is thoughtfully designed to match your child's developmental stage — blending structured learning with imaginative play, so children grow academically, socially, and emotionally in a space they truly love.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {programs.map((p) => (
              <article key={p.name} className="kid-card p-0 overflow-hidden" data-testid={`programs-card-${p.name.toLowerCase()}`}>
                <div className={`h-40 ${p.bg} flex items-center justify-center text-7xl relative`}>
                  <span className="absolute top-4 right-4 text-xs font-semibold text-kid-navy bg-white/80 px-3 py-1 rounded-full">{p.tag}</span>
                  <span className="animate-bounce-soft">{p.icon}</span>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-kid-navy">{p.name}</h3>
                  <p className="text-kid-navy/70 mt-2">{p.desc}</p>
                  <p className="mt-3 text-sm font-semibold text-kid-softblue">{p.timings}</p>
                  <ul className="mt-4 space-y-1">
                    {p.highlights.map((h) => (
                      <li key={h} className="text-sm text-kid-navy/75 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-kid-amber shrink-0" /> {h}
                      </li>
                    ))}
                  </ul>
                  <Link to="/enquiry" className="btn-amber mt-6 inline-flex" data-testid={`programs-enquire-${p.name.toLowerCase()}`}>
                    Enquire Now
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Admission process */}
      <section className="bg-[#F3F4FC] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="section-label">Getting Started</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold text-kid-navy">Simple Admission Process</h2>
            <p className="mt-3 text-kid-navy/70 max-w-xl mx-auto">
              Joining Spring Kids is easy — just three steps and your child's journey begins.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {admissionSteps.map((s) => (
              <div key={s.step} className="kid-card flex flex-col gap-4" data-testid={`admission-step-${s.step}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl ${s.tint} flex items-center justify-center shrink-0`}>
                    <s.icon size={28} strokeWidth={2} className={s.color} />
                  </div>
                  <span className="text-4xl font-bold text-kid-navy/10">{s.step}</span>
                </div>
                <h3 className="font-bold text-kid-navy text-lg">{s.title}</h3>
                <p className="text-sm text-kid-navy/70 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/enquiry" className="btn-amber" data-testid="programs-admission-enquire-btn">
              Start Your Enquiry →
            </Link>
          </div>
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
